import type { Row } from "./api";
import type { Module } from "./catalog";

// datetime-local uses the preparer's local calendar, not a sliced UTC string.
export function assignmentDueInput(value: unknown): string {
  if (typeof value !== "string" || !Number.isFinite(Date.parse(value))) return "";
  const date = new Date(value);
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function assignmentDueError(value: string): string | undefined {
  if (!value) return undefined;
  // Reject silently normalized calendar dates and nonexistent DST local times.
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) ||
      assignmentDueInput(value) !== value)
    return "Enter a valid local due date and time. This time may not exist because of a daylight-saving change.";
  return undefined;
}

export function assignmentDueUpdate(value: string, previous: unknown): string | undefined {
  // Unchanged display values must preserve exact stored precision and DST offset.
  // A blank field also means keep; this workflow does not offer deadline removal.
  if (!value || value === assignmentDueInput(previous)) return undefined;
  const error = assignmentDueError(value);
  if (error) throw new Error(error);
  return new Date(value).toISOString();
}

export const operationStates = [
  "NEW",
  "CLIENT_INVITED",
  "WAITING_FOR_DOCUMENTS",
  "DOCUMENTS_RECEIVED",
  "ASSIGNED",
  "IN_PREPARATION",
  "WAITING_FOR_CLIENT",
  "READY_FOR_REVIEW",
  "REVIEW_IN_PROGRESS",
  "CHANGES_REQUESTED",
  "APPROVED",
  "READY_FOR_SIGNATURE",
  "READY_FOR_FILING",
  "FILED",
  "ACCEPTED",
  "REJECTED",
  "ARCHIVED",
] as const;
const externalStates = new Set([
  "READY_FOR_SIGNATURE",
  "READY_FOR_FILING",
  "FILED",
  "ACCEPTED",
  "REJECTED",
]);
export const isExternalWorkflowState = (state: unknown) =>
  externalStates.has(String(state));
export const workflowLabel = (state: unknown) => {
  if (!state) return "Not started";
  const plain = String(state).toLowerCase().replaceAll("_", " ");
  return plain[0].toUpperCase() + plain.slice(1);
};
export const workflowStatusLabel = (state: unknown) =>
  workflowLabel(state) +
  (isExternalWorkflowState(state) ? " · externally tracked" : "");
export const operationsEnabled = (workspace: Row) =>
  workspace.capabilities?.professionalOperations?.enabled === true;
export const operationsModule: Module = {
  id: "operations",
  label: "Professional Operations",
  icon: "Workflow",
  screens: [
    {
      id: "operations-dashboard",
      path: "/workspace/operations",
      title: "Professional Operations",
      module: "operations",
      kind: "professional-operations",
      icon: "Workflow",
      description:
        "Manage clients, assignments, document requests, reviews and secure communication.",
    },
  ],
};
export const portalModule: Module = {
  id: "portal",
  label: "Client Portal",
  icon: "Files",
  screens: [
    {
      id: "portal-returns",
      path: "/portal/returns",
      title: "Your returns",
      module: "portal",
      kind: "professional-operations",
      icon: "Files",
      description:
        "See your return status, share documents and communicate with your firm.",
    },
  ],
};
export function operationError(error: unknown): string {
  const code = (error as Row)?.code;
  if (
    [
      "REVISION_CONFLICT",
      "VERSION_CONFLICT",
      "WORKFLOW_VERSION_CONFLICT",
      "CONCURRENT_MODIFICATION",
    ].includes(code)
  )
    return "This return changed while you were working. Refresh, review the latest status and try again. Your change was not confirmed.";
  if (
    [
      "FORBIDDEN",
      "PERMISSION_DENIED",
      "WORKFLOW_TRANSITION_FORBIDDEN",
      "OPERATIONS_PERMISSION_DENIED",
      "WORKFLOW_TRANSITION_NOT_PERMITTED",
    ].includes(code)
  )
    return "Your role or current return assignment does not permit this action. Ask your firm administrator to review access.";
  if (code === "AUTHENTICATION_REQUIRED")
    return "Your session has ended. Sign in again before continuing.";
  if (code === "NOT_FOUND" || code === "OPERATIONS_NOT_FOUND")
    return "This record is no longer available to your account. Refresh the workspace.";
  if (code === "STAGE_C_UNAVAILABLE" || code === "OPERATIONS_UNAVAILABLE")
    return "Professional Operations has not been enabled on this server. Ask the administrator to complete the database and readiness checks.";
  if (
    [
      "DOCUMENT_NOT_CLEAN",
      "DOCUMENT_QUARANTINED",
      "DOCUMENT_NOT_AVAILABLE",
      "OPERATIONS_DOCUMENT_NOT_READY",
    ].includes(code)
  )
    return "This document is not available for sharing or download. It must pass the firm's document security checks first.";
  if (code === "MFA_REQUIRED")
    return "Account security — Authenticator: finish authenticator setup or sign in with your current authenticator code before continuing.";
  if (["MFA_INVALID", "INVALID_MFA", "INVALID_MFA_CODE"].includes(code))
    return "Account security — Verification code: the code was not accepted or was already used. Enter a new current authenticator code or an unused recovery code for the account being verified.";
  if (code === "REAUTHENTICATION_REQUIRED")
    return "Account security — Your current password: enter the password for your signed-in administrator account, not a client's password or a new account password.";
  if (code === "REAUTHENTICATION_LOCKED")
    return "Account security — Administrator verification is temporarily locked after repeated unsuccessful attempts. Stop retrying and wait for the 15-minute lock to expire, then use your current password and a fresh verification code.";
  if (
    [
      "REAUTHENTICATION_FAILED",
      "AUTHENTICATION_FAILED",
      "INVALID_CREDENTIALS",
    ].includes(code)
  )
    return "Account security — Verification was not accepted. The response does not identify whether the password or verification code was rejected. Check the account credentials and use a fresh authenticator code or an unused recovery code if required.";
  const messages: Record<string, string> = {
    WORKFLOW_CLIENT_REQUIRED:
      "Return workflow — Client invitation: link this client's return and create an invitation before marking the client invited.",
    WORKFLOW_PREPARER_REQUIRED:
      "Return workflow — Preparer: assign a preparer before moving to the selected status.",
    WORKFLOW_REVIEWER_REQUIRED:
      "Return workflow — Reviewer: assign a reviewer who is not the preparer before moving to review.",
    WORKFLOW_DOCUMENT_REQUESTS_OPEN:
      "Return workflow — Document requests: review and resolve the outstanding requests before review or approval.",
    WORKFLOW_CLEAN_DOCUMENT_REQUIRED:
      "Return workflow — Documents: upload a document and wait for its security scan before marking documents received.",
    WORKFLOW_EXTERNAL_ATTESTATION_REQUIRED:
      "Return workflow — External milestone confirmation: confirm the milestone was verified outside ZentroTax.",
    OPERATIONS_REQUEST_CLOSED:
      "Document request — Response: this request has already been fulfilled or cancelled. Refresh to see its disposition.",
    OPERATIONS_ARTIFACT_APPROVAL_REQUIRED:
      "Completed return — Review: finish the independent operational review before sharing the document with the client.",
    OPERATIONS_CLIENT_ALREADY_BOUND:
      "Client invitation — Portal account: this client already has a linked account. They can sign in with their existing credentials.",
    OPERATIONS_WORKFLOW_NOT_STARTED:
      "Return workflow — Status: your firm must start the return workflow before messages can be sent.",
    OPERATIONS_DUE_DATE_INVALID:
      "Assignment — Due date: enter a valid calendar date and time.",
    OPERATIONS_INDEPENDENT_REVIEW_REQUIRED:
      "Assignment — Reviewer: choose someone other than the assigned preparer.",
    OPERATIONS_SELF_REVIEW_FORBIDDEN:
      "Assignment — Reviewer: choose someone other than the assigned preparer. Preparation and independent review must be assigned to different people.",
    OPERATIONS_ASSIGNMENT_ROLE_INVALID:
      "Assignment — Staff member: choose an active firm member whose role permits the selected preparation or review responsibility.",
    OPERATIONS_ASSIGNMENT_REQUIRED:
      "Return access — Team assignment: you need the appropriate assignment to this return before you can make this change.",
    OPERATIONS_EMAIL_INVALID:
      "Client — Email address: enter a complete email address, such as name@example.com.",
    OPERATIONS_PRIORITY_INVALID:
      "Assignment — Priority: choose Normal, High or Urgent.",
    OPERATIONS_INVITATION_UNAVAILABLE:
      "Client invitation — Access: the invitation service is unavailable. No invitation was issued. Ask your administrator to complete the identity service configuration.",
    WEAK_PASSWORD:
      "Account security — Password: use between 12 and 256 characters.",
    OPERATIONS_VERSION_REQUIRED:
      "Return workflow — Saved version: refresh this return before submitting another change.",
  };
  // Only these bounded, authored domain-validation messages are safe to present.
  // Unknown failures never expose SQL, keys, identifiers, contracts, or raw payloads.
  const safeValidationCodes = new Set([
    "OPERATIONS_FIELD_INVALID",
    "OPERATIONS_TEXT_INVALID",
    "OPERATIONS_ASSIGNMENT_KIND_INVALID",
    "OPERATIONS_CLIENT_UNEXPECTED",
    "OPERATIONS_DOCUMENTS_INVALID",
    "OPERATIONS_MESSAGE_VISIBILITY_INVALID",
    "OPERATIONS_MESSAGES_INVALID",
    "OPERATIONS_REQUEST_STATUS_INVALID",
    "WORKFLOW_EXTERNAL_ATTESTATION_UNEXPECTED",
  ]);
  if (
    safeValidationCodes.has(code) &&
    typeof (error as Row)?.message === "string"
  )
    return (error as Row).message;
  return (
    messages[code] ||
    "The service could not confirm this change. Refresh and check the saved information before retrying. Nothing is marked complete here."
  );
}
export type OperationsQueue =
  "all" | "assignment" | "review" | "missing" | "aging";
export function filterOperationsReturns(
  records: Row[],
  queue: OperationsQueue,
  search: string,
) {
  const term = search.trim().toLowerCase();
  return records.filter((record) => {
    if (
      term &&
      !`${record.name || ""} ${record.reference || ""} ${record.taxYear || ""}`
        .toLowerCase()
        .includes(term)
    )
      return false;
    if (queue === "assignment")
      return !(record.assignments || []).some(
        (a: Row) => a.kind === "PREPARER",
      );
    if (queue === "review")
      return [
        "READY_FOR_REVIEW",
        "REVIEW_IN_PROGRESS",
        "CHANGES_REQUESTED",
      ].includes(record.state);
    if (queue === "missing")
      return Number(record.missingDocumentCount || 0) > 0;
    if (queue === "aging") return record.state !== "ARCHIVED";
    return true;
  });
}
export function operationalAgeDays(updatedAt: unknown, now = Date.now()) {
  if (typeof updatedAt !== "string" || !Number.isFinite(Date.parse(updatedAt)))
    return null;
  return Math.max(0, Math.floor((now - Date.parse(updatedAt)) / 86_400_000));
}
export const canManageOperations = (role: unknown) =>
  ["workspace_admin", "owner"].includes(String(role));
export const canWriteOperations = (role: unknown) =>
  ["workspace_admin", "owner", "preparer", "reviewer"].includes(String(role));
